/**
 * AiCostPanel — "AI-kosten" widget naast het Reply-Rate dashboard.
 * Toont tokens + indicatieve kosten per model-familie (deze maand) en de
 * besparing van Kimi t.o.v. Claude Sonnet.
 * Data: GET /ai-usage/summary
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { Coins, ChevronDown, ChevronUp, TrendingDown } from "lucide-react";

const FAMILY_META = {
    kimi: { label: "Kimi", color: "#14e0c8" },
    claude: { label: "Claude", color: "#d97706" },
};

const fmtCost = (c) => (c > 0 && c < 0.01 ? "< $0.01" : `$${(c || 0).toFixed(2)}`);
const fmtTokens = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n || 0}`);

export default function AiCostPanel() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        api.get("/ai-usage/summary").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data || !(data.families || []).length) return null;
    const anyEstimated = data.families.some((f) => f.estimated);
    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)] px-5 py-4" data-testid="ai-cost-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="ai-cost-toggle">
                <Coins size={14} className="text-[var(--jr-brand)]" />
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)]">
                    {i18n.t("aicosts.title")} · {data.month}
                </span>
                <span className="text-[10px] font-mono text-[var(--jr-text-3)]" data-testid="ai-cost-total">
                    {data.totals.calls} {i18n.t("aicosts.calls")} · {fmtCost(data.totals.cost_usd)}
                </span>
                {data.kimi_savings_usd > 0 && (
                    <span className="text-[10px] font-mono text-[#22c55e] inline-flex items-center gap-1" data-testid="ai-cost-savings-badge">
                        <TrendingDown size={10} /> {fmtCost(data.kimi_savings_usd)} {i18n.t("aicosts.saved")}
                    </span>
                )}
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="mt-3 space-y-1.5" data-testid="ai-cost-rows">
                    {data.families.map((f) => {
                        const meta = FAMILY_META[f.family] || { label: f.family, color: "#8b8b96" };
                        const share = data.totals.cost_usd > 0 ? (f.cost_usd / data.totals.cost_usd) * 100 : 0;
                        return (
                            <div key={f.family} className="flex items-center gap-2" data-testid={`ai-cost-row-${f.family}`}>
                                <span className="text-[11px] text-[var(--jr-text-2)] w-24 truncate inline-flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
                                    {meta.label}
                                </span>
                                <span className="text-[10px] font-mono text-[var(--jr-text-3)] w-40 shrink-0">
                                    {f.calls} {i18n.t("aicosts.calls")} · {fmtTokens(f.prompt_tokens)} {i18n.t("aicosts.tok_in")} · {fmtTokens(f.completion_tokens)} {i18n.t("aicosts.tok_out")}
                                </span>
                                <div className="flex-1 h-1.5 rounded-full bg-white/6 overflow-hidden">
                                    <div className="h-full rounded-full transition-all"
                                        style={{ width: `${Math.min(share, 100)}%`, background: meta.color }} />
                                </div>
                                <span className="text-[10px] font-mono w-16 text-right shrink-0 text-[var(--jr-text-2)]">
                                    {fmtCost(f.cost_usd)}{f.estimated ? " *" : ""}
                                </span>
                            </div>
                        );
                    })}
                    {data.kimi_savings_usd > 0 && (
                        <p className="text-[10px] text-[#22c55e] pt-1" data-testid="ai-cost-savings-line">
                            {i18n.t("aicosts.savings_line", { amount: fmtCost(data.kimi_savings_usd) })}
                        </p>
                    )}
                    {anyEstimated && (
                        <p className="text-[10px] text-[var(--jr-text-3)] italic">{i18n.t("aicosts.estimated_note")}</p>
                    )}
                </div>
            )}
        </div>
    );
}
